// ── Intercept recovery tokens BEFORE Supabase client loads ──
// Supabase JS strips hash tokens on init. Redirect to /reset-password first.
const __hash = window.location.hash;
if (__hash && __hash.includes('type=recovery') && !window.location.pathname.includes('reset-password')) {
  window.location.replace(`/reset-password${__hash}`);
}

import { createRoot } from "react-dom/client";
import "./index.css";

const root = createRoot(document.getElementById("root")!);

function isRootRoute() {
  return window.location.pathname === '/' && !window.location.hash;
}

function hasSupabaseSessionHint() {
  try {
    return Object.keys(window.localStorage).some((key) => key.startsWith('sb-') && key.endsWith('-auth-token'));
  } catch {
    return false;
  }
}

function suppressNativeSelectionChrome() {
  document.addEventListener('contextmenu', (event) => {
    const tag = (event.target as HTMLElement)?.tagName;
    if (tag !== 'INPUT' && tag !== 'TEXTAREA') {
      event.preventDefault();
    }
  });

  document.addEventListener('selectstart', (event) => {
    const tag = (event.target as HTMLElement)?.tagName;
    if (tag !== 'INPUT' && tag !== 'TEXTAREA') {
      event.preventDefault();
    }
  });
}

async function renderFullApp(isNative: boolean) {
  const [{ ThemeProvider }, { default: App }] = await Promise.all([
    import("next-themes"),
    import("./App.tsx"),
  ]);

  const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (sentryDsn) {
    const Sentry = await import("@sentry/react");
    Sentry.init({
      dsn: sentryDsn,
      environment: import.meta.env.MODE,
      tracesSampleRate: 0.1,
    });
  }

  const posthogKey = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  const posthogHost = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://us.i.posthog.com';
  if (posthogKey) {
    const { default: posthog } = await import("posthog-js");
    posthog.init(posthogKey, {
      api_host: posthogHost,
      capture_pageview: true,
      capture_pageleave: true,
      person_profiles: 'identified_only',
    });
  }

  const crispWebsiteId = import.meta.env.VITE_CRISP_WEBSITE_ID as string | undefined;
  if (crispWebsiteId) {
    const w = window as Window & { $crisp?: unknown[]; CRISP_WEBSITE_ID?: string };
    w.$crisp = w.$crisp || [];
    w.CRISP_WEBSITE_ID = crispWebsiteId;
    const script = document.createElement('script');
    script.src = 'https://client.crisp.chat/l.js';
    script.async = true;
    document.head.appendChild(script);
  }

  const { initializeMobile } = await import("./lib/mobile.ts");
  initializeMobile();

  if ('serviceWorker' in navigator && import.meta.env.PROD && !isNative) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch((error) => {
        console.error('Service worker registration failed:', error);
      });
    });
  }

  root.render(
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <App />
    </ThemeProvider>
  );
}

async function renderWaitlistOrFullApp(isNative: boolean) {
  if (hasSupabaseSessionHint()) {
    await renderFullApp(isNative);
    return;
  }

  const { default: LandingPage } = await import("./pages/LandingPage");
  root.render(<LandingPage />);
}

async function bootstrap() {
  suppressNativeSelectionChrome();

  const { Capacitor } = await import("@capacitor/core");
  const isNative = Capacitor.isNativePlatform();

  if (isRootRoute() && !isNative) {
    await renderWaitlistOrFullApp(isNative);
    return;
  }

  await renderFullApp(isNative);
}

void bootstrap();
